import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import type { AdminTrialItem } from '../types/adminTrial';
import {
  getTrialActionAvailability,
  getTrialActionDisabledReason,
  type TrialActionKey,
} from '../utils/trialHelpers';

interface TrialAdminActionsProps {
  trial: AdminTrialItem;
  identityConfirmed: boolean;
  canSuspend: boolean;
  canCancel: boolean;
  canUpdate: boolean;
  isPending: boolean;
  onExtend: () => void;
  onSuspend: () => void;
  onResume: () => void;
  onCancel: () => void;
}

type ActionConfig = {
  key: TrialActionKey;
  labelKey: string;
  variant: 'outline' | 'destructive';
  show: boolean;
  onClick: () => void;
};

export function TrialAdminActions({
  trial,
  identityConfirmed,
  canSuspend,
  canCancel,
  canUpdate,
  isPending,
  onExtend,
  onSuspend,
  onResume,
  onCancel,
}: TrialAdminActionsProps) {
  const { t } = useTranslation();
  const availability = useMemo(() => getTrialActionAvailability(trial), [trial]);

  const context = useMemo(
    () => ({
      identityConfirmed,
      canUpdate,
      canSuspend,
      canCancel,
    }),
    [identityConfirmed, canUpdate, canSuspend, canCancel],
  );

  const actions: ActionConfig[] = [
    {
      key: 'extend',
      labelKey: 'trials.actions.extend',
      variant: 'outline',
      show: canUpdate,
      onClick: onExtend,
    },
    {
      key: 'suspend',
      labelKey: 'subscriptions.actions.suspend',
      variant: 'outline',
      show: canSuspend,
      onClick: onSuspend,
    },
    {
      key: 'resume',
      labelKey: 'subscriptions.actions.resume',
      variant: 'outline',
      show: canSuspend,
      onClick: onResume,
    },
    {
      key: 'cancel',
      labelKey: 'trials.actions.cancelTrial',
      variant: 'destructive',
      show: canCancel,
      onClick: onCancel,
    },
  ];

  const visibleActions = actions.filter((a) => a.show);
  const anyAvailable =
    (canUpdate && availability.extend) ||
    (canSuspend && availability.suspend) ||
    (canSuspend && availability.resume) ||
    (canCancel && availability.cancel);

  if (visibleActions.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('trials.actions.disabled.noPermission')}</p>
    );
  }

  return (
    <div className="space-y-3">
      {!anyAvailable && identityConfirmed ? (
        <p className="text-xs text-muted-foreground">{t('trials.actions.disabled.notAvailable')}</p>
      ) : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visibleActions.map((action) => {
          const disabledReason = getTrialActionDisabledReason(action.key, trial, context, t);
          const isEnabled =
            disabledReason === null &&
            ((action.key === 'extend' && availability.extend) ||
              (action.key === 'suspend' && availability.suspend) ||
              (action.key === 'resume' && availability.resume) ||
              (action.key === 'cancel' && availability.cancel));

          return (
            <div key={action.key} className="min-w-0 space-y-1">
              <Button
                variant={action.variant}
                size="sm"
                className={cn(
                  'w-full justify-center',
                  action.variant === 'outline' && 'font-medium',
                )}
                disabled={!isEnabled || isPending}
                onClick={action.onClick}
                title={disabledReason ?? undefined}
                aria-describedby={disabledReason ? `${action.key}-disabled-reason` : undefined}
              >
                {t(action.labelKey)}
              </Button>
              {disabledReason ? (
                <p
                  id={`${action.key}-disabled-reason`}
                  className="text-xs text-muted-foreground leading-snug px-0.5"
                >
                  {disabledReason}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
